"use client";

import { useState } from "react";
import { X, LogIn, Key, ExternalLink, Check, ChevronRight } from "lucide-react";
import { useRouter } from "next/navigation";
import { supportsOAuth, getOAuthProvider, initiateOAuth } from "@/lib/integrations/oauth";
import { getIntegrationAuthConfig } from "@/lib/integrations/oauth-mapping";
import IntegrationConnectionFlow from "@/components/integrations/IntegrationConnectionFlow";
import type { IntegrationType } from "@/types/integration";
import type { Integration } from "@/types/integration";
import ProductIcon from "@/components/ui/ProductIcon";

interface IntegrationListItem {
  type: IntegrationType;
  name: string;
  description: string;
  icon: string;
  category: string;
  capability: any;
  isOAuth: boolean;
  isFree: boolean;
  price?: number;
  isConnected: boolean;
}

interface Props {
  integration: IntegrationListItem;
  connectedIntegration?: Integration;
  onClose: () => void;
  onConnect: (integrationType: IntegrationType, config: Record<string, any>) => Promise<void>;
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  textSecondaryColor: string;
}

export default function IntegrationDetailModal({
  integration,
  connectedIntegration,
  onClose,
  onConnect,
  primaryColor,
  secondaryColor,
  textColor,
  textSecondaryColor,
}: Props) {
  const router = useRouter();
  const [connectionMethod, setConnectionMethod] = useState<"oauth" | "api-key">("oauth");
  const [showApiDocs, setShowApiDocs] = useState(false);

  const isConnected = connectedIntegration && Object.keys(connectedIntegration.config || {}).length > 0;
  const provider = getOAuthProvider(integration.type);
  const authConfig = getIntegrationAuthConfig(integration.type);
  const hasApiKeyOption = authConfig.supportsApiKey || !!integration.capability?.documentationUrl;
  const apiKeyOnly = !integration.isOAuth && hasApiKeyOption;

  const handleOAuthConnect = () => {
    if (!provider) {
      setConnectionMethod("api-key");
      return;
    }

    const state = `${integration.type}:${Date.now()}`;
    if (typeof window !== "undefined") {
      localStorage.setItem("oauth_state", state);
      localStorage.setItem("oauth_integration_type", integration.type);
    }
    initiateOAuth(provider.id, state);
  };

  const handleApiKeyConnect = () => {
    setConnectionMethod("api-key");
  };

  const primaryButtonStyle = {
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    boxShadow: `0 4px 16px ${primaryColor}40`,
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-200 animate-in fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
        aria-modal
        role="dialog"
      >
        <div
          className="w-full max-w-md rounded-2xl border overflow-hidden max-h-[90vh] overflow-y-auto bg-white shadow-2xl pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-200"
          style={{ borderColor: `${primaryColor}40` }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                style={{ background: `linear-gradient(135deg, ${primaryColor}20 0%, ${secondaryColor}20 100%)` }}
              >
                <ProductIcon icon={integration.icon} size={32} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{integration.name}</h2>
                <p className="text-xs text-gray-500">{integration.category}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - dark text on light for readability */}
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900">{integration.capability?.name || integration.name}</h3>
              <p className="text-sm leading-relaxed text-gray-600">{integration.description}</p>
            </div>

            {/* Connection Status */}
            {isConnected && (
              <div className="p-4 rounded-xl flex items-center gap-3 bg-green-50 border border-green-200">
                <Check className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Connected</p>
                  <p className="text-xs text-gray-600">This plugin is connected and ready to use in chat.</p>
                </div>
              </div>
            )}

            {/* Connect: one primary CTA (OAuth or API key), optional secondary link */}
            {!isConnected && (integration.isFree || apiKeyOnly) && (
              <div className="space-y-3">
                {provider && !apiKeyOnly && (
                  <button
                    onClick={handleOAuthConnect}
                    className="w-full px-4 py-3 rounded-xl text-white font-medium transition-all hover:opacity-95 active:scale-[0.98] flex items-center justify-center gap-2"
                    style={primaryButtonStyle}
                  >
                    <LogIn className="w-5 h-5" />
                    Sign in with {integration.name}
                  </button>
                )}
                {hasApiKeyOption && (apiKeyOnly || !provider) && (
                  <button
                    onClick={handleApiKeyConnect}
                    className="w-full px-4 py-3 rounded-xl font-medium transition-all hover:opacity-95 active:scale-[0.98] flex items-center justify-center gap-2"
                    style={
                      apiKeyOnly || !provider
                        ? { ...primaryButtonStyle, color: "white" }
                        : { border: `2px solid ${primaryColor}`, color: primaryColor, background: "transparent" }
                    }
                  >
                    <Key className="w-5 h-5" />
                    {apiKeyOnly || !provider ? "Connect with API Key" : "Use API Key instead"}
                  </button>
                )}
                {hasApiKeyOption && provider && !apiKeyOnly && (
                  <button
                    type="button"
                    onClick={handleApiKeyConnect}
                    className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Or connect with API key
                  </button>
                )}
              </div>
            )}

            {/* Purchase Button - Paid */}
            {!integration.isFree && !isConnected && integration.price && (
              <div className="space-y-3">
                <button
                  onClick={() => router.push(`/marketplace/purchase?integration=${integration.type}`)}
                  className="w-full px-4 py-3 rounded-xl text-white font-medium transition-all hover:opacity-95 active:scale-[0.98] flex items-center justify-center gap-2"
                  style={primaryButtonStyle}
                >
                  Purchase for ${integration.price}
                </button>
                {integration.isOAuth && (
                  <p className="text-xs text-center" style={{ color: textSecondaryColor }}>
                    After purchase, you can connect via OAuth or API key
                  </p>
                )}
              </div>
            )}

            {/* Information Section */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Category:</span>
                  <span className="text-gray-900">{integration.category}</span>
                </div>
                {integration.capability?.actionTypes && (
                  <div className="flex justify-between text-gray-600">
                    <span>Capabilities:</span>
                    <span className="text-gray-900">{integration.capability.actionTypes.join(", ")}</span>
                  </div>
                )}
                {integration.capability?.riskLevel && (
                  <div className="flex justify-between text-gray-600">
                    <span>Risk Level:</span>
                    <span className="text-gray-900">{integration.capability.riskLevel}</span>
                  </div>
                )}
              </div>
            </div>

            {/* API Access & Documentation */}
            {hasApiKeyOption && (
              <div className="space-y-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowApiDocs(!showApiDocs)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition text-gray-900"
                >
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    <span className="text-sm font-medium">API Access & Documentation</span>
                  </div>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showApiDocs ? "rotate-90" : ""}`} />
                </button>
                {showApiDocs && (
                  <div className="space-y-2 pl-6">
                    <p className="text-xs text-gray-600">
                      Use API keys for programmatic access. Documentation includes setup guides and API reference.
                    </p>
                    {integration.capability?.documentationUrl && (
                      <a
                        href={integration.capability.documentationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Documentation
                      </a>
                    )}
                    {!isConnected && (
                      <button
                        onClick={handleApiKeyConnect}
                        className="w-full mt-2 px-4 py-2 rounded-lg border border-indigo-200 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center justify-center gap-2"
                      >
                        <Key className="w-4 h-4" />
                        Connect with API Key
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* API Key Connection Flow */}
      {connectionMethod === "api-key" && (
        <IntegrationConnectionFlow
          integrationType={integration.type}
          integrationName={integration.name}
          onClose={() => setConnectionMethod("oauth")}
          onConnect={async (config) => {
            await onConnect(integration.type, config);
            setConnectionMethod("oauth");
          }}
        />
      )}
    </>
  );
}
